import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { hasRouteAccess, syncPrivilegesFromBackend, getRolePrivileges } from '../services/privilegeService';

/**
 * PrivilegedRoute component checks if the current user has permission to access a specific route
 * It will redirect to login if not authenticated or to an unauthorized page if authenticated but not authorized
 * 
 * @param {Object} props Component props
 * @param {string} props.path The route path to check against stored privileges
 * @param {JSX.Element} props.children The components to render if authorized
 * @returns {JSX.Element} The children or a redirect
 */
const PrivilegedRoute = ({ path, children }) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkAuthorization();
  }, [path, location]);

  const checkAuthorization = async () => {
    // First check if token is valid
    const isAuth = authService.isAuthenticated();
    if (!isAuth) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    try {
      const userRole = authService.getUserRole();
      let privilegesSynced = false;
      
      // CRITICAL: If privileges are missing, sync from backend first
      if (userRole && userRole !== 'ADMIN') {
        const privileges = getRolePrivileges(userRole);
        const hasPrivileges = privileges.visibleMenus?.length > 0 || privileges.accessibleRoutes?.length > 0;
        
        if (!hasPrivileges && !syncing) {
          console.log(`[PrivilegedRoute] ⚠️ No privileges found for role ${userRole} - syncing from backend...`);
          setSyncing(true);
          try {
            await syncPrivilegesFromBackend(userRole);
            console.log(`[PrivilegedRoute] ✅ Privileges synced successfully`);
            
            // Import fixPrivilegeRoutes to ensure routes are properly formatted
            const { fixPrivilegeRoutes } = await import('../services/privilegeService');
            fixPrivilegeRoutes(userRole);
            privilegesSynced = true;
          } catch (syncError) {
            console.error('[PrivilegedRoute] ❌ Failed to sync privileges:', syncError);
            console.error('[PrivilegedRoute] Sync error details:', syncError.response?.data || syncError.message);
          } finally {
            setSyncing(false);
          }
        }
      }
      
      // CRITICAL FIX: Use the actual location path, not the route pattern
      // React Router passes route patterns like "/sales/*" but we need to check the actual path
      const actualPath = location.pathname;
      const routeToCheck = actualPath || path;
      
      console.log(`[PrivilegedRoute] Checking access for route pattern: ${path}, actual path: ${actualPath}`);
      
      // Check if user has access to this route using the privilege service
      const hasAccess = hasRouteAccess(routeToCheck);
      console.log(`Path access check for ${routeToCheck}: ${hasAccess}`);
      setIsAuthorized(hasAccess);
      
      // If we just synced privileges, we might need to re-render to show the correct page
      if (privilegesSynced && hasAccess) {
        // Force a re-check by updating state
        setIsAuthorized(true);
      }
    } catch (error) {
      console.error('Error checking route authorization:', error);
      setIsAuthorized(false);
    }
    
    setLoading(false);
  };

  if (loading || syncing) {
    return <div>Loading...</div>;
  }

  if (!authService.isAuthenticated()) {
    // Redirect to login with return path
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return isAuthorized ? children : <Navigate to="/unauthorized" replace />;
};

export default PrivilegedRoute; 